/**
 * Customer Data Service
 *
 * High-level service for decrypting customer data using the DEK.
 * This is the main interface for components to decrypt order history,
 * appointment details, and profile information.
 *
 * Usage Flow:
 * 1. Customer unlocks app with passphrase → DEK is unwrapped
 * 2. DEK is stored in memory (EncryptionContext)
 * 3. Components call these functions with encrypted data from API
 * 4. Data is decrypted client-side and returned
 *
 * NOTE: This service is READ-ONLY. Customers cannot encrypt data.
 */

import type { EncryptedData } from '@/types/crypto'
import { cryptoService } from './crypto.service'

/**
 * Encrypted field wrapper
 *
 * Used for individual encrypted fields within a larger object.
 */
export interface EncryptedField<T = unknown> {
  encrypted: EncryptedData
  _type?: T // Type hint for TypeScript (never actually stored)
}

/**
 * Encrypted record from API
 *
 * Standard format for encrypted records returned from the backend.
 * Some APIs return a mix of encrypted data (ciphertext) and plain-text
 * metadata (like startTime, endTime). The plainTextFields are merged
 * with the decrypted data when calling decryptRecords().
 */
export interface EncryptedRecord {
  id: string
  encryptedData: EncryptedData
  metadata?: {
    createdAt: string
    updatedAt: string
    version?: number
    dekId?: string // DEK identifier for rotation support
  }
  /**
   * Plain-text fields from the API that should be merged with decrypted data.
   * Use this for fields like startTime, endTime, appointmentType that are
   * stored unencrypted in the database but need to be part of the result object.
   */
  plainTextFields?: Record<string, unknown>
}

/**
 * Decryption error with context
 */
export class DecryptionError extends Error {
  public readonly recordId?: string
  public readonly fieldName?: string

  constructor(message: string, options?: { recordId?: string; fieldName?: string }) {
    super(message)
    this.name = 'DecryptionError'
    this.recordId = options?.recordId
    this.fieldName = options?.fieldName
  }
}

/**
 * Customer Data Service
 */
class CustomerDataService {
  /**
   * Decrypt a single record
   *
   * @param encryptedRecord - Encrypted record from API
   * @param dek - Data Encryption Key (from EncryptionContext)
   * @returns Decrypted data of type T
   * @throws DecryptionError if decryption fails
   */
  async decryptRecord<T>(encryptedRecord: EncryptedRecord, dek: Uint8Array): Promise<T> {
    try {
      return await cryptoService.decrypt<T>(encryptedRecord.encryptedData, dek)
    } catch (error) {
      throw new DecryptionError(`Failed to decrypt record ${encryptedRecord.id}: ${error}`, {
        recordId: encryptedRecord.id,
      })
    }
  }

  /**
   * Decrypt multiple records
   *
   * @param encryptedRecords - Array of encrypted records from API
   * @param dek - Data Encryption Key
   * @returns Array of decrypted data with original metadata and plain-text fields merged
   */
  async decryptRecords<T>(
    encryptedRecords: EncryptedRecord[],
    dek: Uint8Array
  ): Promise<Array<T & { _id: string; _metadata?: EncryptedRecord['metadata'] }>> {
    const results = await Promise.all(
      encryptedRecords.map(async (record) => {
        let decrypted: T
        try {
          decrypted = await this.decryptRecord<T>(record, dek)
        } catch {
          // If decryption fails and we have plainTextFields, use empty object as fallback
          // This handles cases where backend sends minimal/invalid ciphertext but includes
          // the actual data as plain-text fields
          if (record.plainTextFields && Object.keys(record.plainTextFields).length > 0) {
            decrypted = {} as T
          } else {
            throw new DecryptionError(`Failed to decrypt record ${record.id}`, {
              recordId: record.id,
            })
          }
        }
        return {
          // First spread plain-text fields (if any) so encrypted data can override if needed
          ...record.plainTextFields,
          // Then spread decrypted data
          ...decrypted,
          _id: record.id,
          _metadata: record.metadata,
        }
      })
    )
    return results
  }

  /**
   * Decrypt raw encrypted data
   *
   * For cases where you just have EncryptedData without the record wrapper.
   *
   * @param encryptedData - Encrypted data
   * @param dek - Data Encryption Key
   * @returns Decrypted data of type T
   */
  async decryptData<T>(encryptedData: EncryptedData, dek: Uint8Array): Promise<T> {
    try {
      return await cryptoService.decrypt<T>(encryptedData, dek)
    } catch (error) {
      throw new DecryptionError(`Failed to decrypt data: ${error}`)
    }
  }

  /**
   * Decrypt an individual field from an object
   *
   * For objects that have some encrypted and some unencrypted fields.
   *
   * @param encryptedField - Encrypted field value
   * @param dek - Data Encryption Key
   * @returns Decrypted field value
   */
  async decryptField<T>(encryptedField: EncryptedField<T>, dek: Uint8Array): Promise<T> {
    try {
      return await cryptoService.decrypt<T>(encryptedField.encrypted, dek)
    } catch (error) {
      throw new DecryptionError(`Failed to decrypt field: ${error}`)
    }
  }

  /**
   * Decrypt an object with selective encrypted fields
   *
   * Some API responses have a mix of encrypted and plain fields.
   * This function decrypts only the specified fields.
   *
   * @param obj - Object with some encrypted fields
   * @param encryptedFields - Array of field names that are encrypted
   * @param dek - Data Encryption Key
   * @returns Object with decrypted fields
   */
  async decryptSelectiveFields<T extends Record<string, unknown>>(
    obj: T,
    encryptedFields: (keyof T)[],
    dek: Uint8Array
  ): Promise<T> {
    const result = { ...obj }

    for (const fieldName of encryptedFields) {
      const encryptedValue = obj[fieldName] as EncryptedData | undefined
      if (encryptedValue && this.isEncryptedData(encryptedValue)) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result[fieldName] = (await cryptoService.decrypt(encryptedValue, dek)) as any
        } catch (error) {
          throw new DecryptionError(`Failed to decrypt field "${String(fieldName)}": ${error}`, {
            fieldName: String(fieldName),
          })
        }
      }
    }

    return result
  }

  /**
   * Check if a value looks like encrypted data
   *
   * @param value - Value to check
   * @returns True if the value appears to be encrypted data
   */
  isEncryptedData(value: unknown): value is EncryptedData {
    if (!value || typeof value !== 'object') {
      return false
    }
    const obj = value as Record<string, unknown>
    return typeof obj.ciphertext === 'string' && typeof obj.iv === 'string'
  }

  /**
   * Safely attempt decryption with fallback
   *
   * Returns null instead of throwing if decryption fails.
   * Useful for optional encrypted fields.
   *
   * @param encryptedData - Encrypted data (may be null/undefined)
   * @param dek - Data Encryption Key
   * @returns Decrypted data or null
   */
  async safeDecrypt<T>(encryptedData: EncryptedData | null | undefined, dek: Uint8Array): Promise<T | null> {
    if (!encryptedData) {
      return null
    }

    try {
      return await cryptoService.decrypt<T>(encryptedData, dek)
    } catch {
      return null
    }
  }

  /**
   * Decrypt customer profile data
   *
   * Convenience method for profile decryption.
   *
   * @param encryptedProfile - Encrypted profile from API
   * @param dek - Data Encryption Key
   * @returns Decrypted profile
   */
  async decryptProfile<T>(encryptedProfile: EncryptedRecord, dek: Uint8Array): Promise<T> {
    return this.decryptRecord<T>(encryptedProfile, dek)
  }

  /**
   * Decrypt order data
   *
   * Convenience method for order decryption.
   *
   * @param encryptedOrder - Encrypted order from API
   * @param dek - Data Encryption Key
   * @returns Decrypted order
   */
  async decryptOrder<T>(encryptedOrder: EncryptedRecord, dek: Uint8Array): Promise<T> {
    return this.decryptRecord<T>(encryptedOrder, dek)
  }

  /**
   * Decrypt appointment data
   *
   * Convenience method for appointment decryption.
   *
   * @param encryptedAppointment - Encrypted appointment from API
   * @param dek - Data Encryption Key
   * @returns Decrypted appointment
   */
  async decryptAppointment<T>(encryptedAppointment: EncryptedRecord, dek: Uint8Array): Promise<T> {
    return this.decryptRecord<T>(encryptedAppointment, dek)
  }
}

// Export singleton instance
export const customerDataService = new CustomerDataService()

// Export the DecryptionError for use in error handling
export { DecryptionError as CustomerDataDecryptionError }
